import React, { useState } from "react";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { MoreVert } from "@mui/icons-material";

const buildSx = (baseStyles, customStyles) => {
  if (!customStyles) {
    return [baseStyles];
  }

  return Array.isArray(customStyles)
    ? [baseStyles, ...customStyles]
    : [baseStyles, customStyles];
};

export default function PopoverMenuList({
  items = [],
  icon = <MoreVert />,
  buttonProps,
  menuProps,
  menuItemProps,
  menuSx,
  stopPropagation = true,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    if (stopPropagation) {
      event.stopPropagation();
    }

    buttonProps?.onClick?.(event);

    if (event.defaultPrevented) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (stopPropagation) {
      event?.stopPropagation?.();
    }

    setAnchorEl(null);
    menuProps?.onClose?.(event);
  };

  const handleItemClick = (item) => (event) => {
    if (stopPropagation) {
      event.stopPropagation();
    }

    item?.onClick?.(event);

    if (!event.defaultPrevented && item?.closeOnClick !== false) {
      handleClose(event);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        {...buttonProps}
        onClick={handleOpen}
      >
        {icon}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableScrollLock={true}
        {...menuProps}
        slotProps={{
          ...menuProps?.slotProps,
          paper: {
            ...menuProps?.slotProps?.paper,
            sx: buildSx(
              {
                boxShadow: theme.shadows[2],
                minWidth: 100,
                borderRadius: 1,
              },
              menuProps?.slotProps?.paper?.sx || menuSx,
            ),
          },
        }}
      >
        {items.map((item, index) => (
          <MenuItem
            key={item?.key || item?.label || index}
            {...menuItemProps}
            {...item?.menuItemProps}
            disabled={item?.disabled}
            onClick={handleItemClick(item)}
            sx={buildSx(
              {
                py: 1,
                px: 2,
                fontSize: 14,
              },
              item?.menuItemProps?.sx || menuItemProps?.sx,
            )}
          >
            {item?.icon && (
              <ListItemIcon sx={{ minWidth: 36 }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item?.label}
              primaryTypographyProps={{ fontSize: 14 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
